import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Token may already be expired — proceed with local cleanup
    }
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) return null;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/refresh`, {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: newRefreshToken, user: userData } = response.data;
      
      setAccessToken(access_token);
      setRefreshToken(newRefreshToken);
      setUser(userData);
      
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      return access_token;
    } catch (error) {
      logout();
      return null;
    }
  }, [refreshToken, logout]);

  const fetchUser = useCallback(async () => {
    if (!accessToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) {
          logout();
        }
      }
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshAccessToken, logout]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    setUser(userData);
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, userData);
    const { access_token, refresh_token, user: newUser } = response.data;
    
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    setUser(newUser);
    
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    
    return newUser;
  };

  const getAuthHeaders = useCallback(() => {
    return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  }, [accessToken]);

  const value = {
    user,
    loading,
    login,
    logout,
    register,
    accessToken,
    getAuthHeaders,
    refreshAccessToken,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'super_admin' || user?.role === 'org_admin',
    isSuperAdmin: user?.role === 'super_admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
